import { computed, inject, Injectable, signal } from '@angular/core';
import { ApiErrorResponse, ErrorHandlerV2Service } from '@gea/digital-ui-lib';
import { isEqual as equal } from 'lodash';
import { catchError, defer, EMPTY, first, forkJoin, map, Observable, of, tap } from 'rxjs';

import { ProblemSolvingApiService, TroubleshootingApiService } from '@shared/services';
import {
  CreateRecommendationParams,
  FetchRecommendationsParams,
  RecommendationDto,
  RecommendationFaultStatus,
  SetRecommendationStatusParams,
  SolveProblemParams,
  TroubleshootingDto,
  TroubleshootingStateModel,
} from '@shared/models';
import { FaultsStore } from '../faults';

const INITIAL_STATE: TroubleshootingStateModel = { problemSolvedSuccess: false, isLoadingRecommendations: false };

const emptyTroubleshooting = (): TroubleshootingDto => ({
  recommendationText: 'n.a.',
  recommendations: [],
  status: RecommendationFaultStatus.PENDING,
});

@Injectable({ providedIn: 'root' })
export class TroubleshootingStore {
  private readonly troubleshootingApi = inject(TroubleshootingApiService);
  private readonly problemSolvingApi = inject(ProblemSolvingApiService);
  private readonly errorHandler = inject(ErrorHandlerV2Service);
  private readonly faultsStore = inject(FaultsStore);

  private readonly state = signal<TroubleshootingStateModel>(INITIAL_STATE);

  readonly recommendationText = computed<string | undefined>(() => this.state().recommendationText);
  readonly recommendations = computed<RecommendationDto[] | undefined>(() => this.state().recommendations, {
    equal,
  });
  readonly approvedRecommendations = computed<RecommendationDto[] | undefined>(() => this.state().approvedRecommendations, {
    equal,
  });
  readonly recommendationFaultStatus = computed<RecommendationFaultStatus | undefined>(() => this.state().status);
  readonly faultCode = computed<number | undefined>(() => this.state().faultCode);
  readonly machine = computed<number | undefined>(() => this.state().machine);
  readonly problemSolvedSuccess = computed<boolean>(() => this.state().problemSolvedSuccess);
  readonly isLoadingRecommendations = computed<boolean>(() => this.state().isLoadingRecommendations);

  /**
   * Loads the recommendations and the approved recommendations for a machine and fault code.
   * Both lists are cleared before the requests are sent.
   */
  fetchRecommendations({ machine, faultCode }: FetchRecommendationsParams): void {
    this.clearRecommendations();
    this.patch({ approvedRecommendations: undefined, isLoadingRecommendations: true });

    const recommendations$ = this.troubleshootingApi.fetchRecommendations({ machine, faultCode }).pipe(
      catchError((error: ApiErrorResponse) => {
        this.reportFetchError(error);
        return of(emptyTroubleshooting());
      }),
      first()
    );

    const approvedRecommendations$ = this.troubleshootingApi.fetchApprovedRecommendations({ machine, faultCode }).pipe(
      catchError((error: ApiErrorResponse) => {
        this.reportFetchError(error);
        return of(emptyTroubleshooting());
      }),
      first()
    );

    forkJoin({ recommendations: recommendations$, approvedRecommendations: approvedRecommendations$ }).subscribe(
      ({ recommendations, approvedRecommendations }) => {
        this.patch({
          recommendationText: recommendations.recommendationText,
          recommendations: recommendations.recommendations,
          status: recommendations.status,
          approvedRecommendations: approvedRecommendations.recommendations,
          isLoadingRecommendations: false,
        });
      }
    );
  }

  fetchRecommendationsByFaultCode(faultCode: number): void {
    this.clearRecommendations();

    this.troubleshootingApi
      .fetchRecommendationsByFaultCode(faultCode)
      .pipe(
        catchError((error: ApiErrorResponse) => {
          this.reportFetchError(error);
          return of([] as RecommendationDto[]);
        }),
        first()
      )
      .subscribe((recommendations) => {
        this.patch({ recommendations });
      });
  }

  setSelectedMachine(machine?: number): void {
    this.patch({ machine });

    if (machine) {
      this.faultsStore.fetchFaultCodes({ machines: [machine] });
    }
  }

  setFaultCode(faultCode?: number): void {
    this.patch({ faultCode });
  }

  resetParams(): void {
    this.patch({ faultCode: undefined, machine: undefined });
  }

  clearRecommendations(): void {
    this.patch({ recommendationText: undefined, recommendations: undefined, isLoadingRecommendations: false });
  }

  /**
   * Persists the new order of the recommendations. The local list is updated after the API call succeeds.
   * The caller must subscribe to trigger the request.
   */
  reorderRecommendations(recommendations: RecommendationDto[]): Observable<void> {
    this.patch({ isLoadingRecommendations: true });
    return defer(() => this.troubleshootingApi.updateRecommendationsOrder(recommendations)).pipe(
      tap(() => this.patch({ recommendations, isLoadingRecommendations: false })),
      map(() => undefined),
      catchError((error: ApiErrorResponse) => {
        this.patch({ isLoadingRecommendations: false });
        return this.handleError(error);
      })
    );
  }

  /**
   * Sets the status of a recommendation and reloads the recommendations of the machine and fault code.
   * The caller must subscribe to trigger the request.
   */
  setRecommendationStatus({
    machine,
    faultCode,
    recommendationId,
    recommendationStatus,
  }: SetRecommendationStatusParams): Observable<void> {
    return defer(() =>
      this.troubleshootingApi.setRecommendationStatus(machine, faultCode, recommendationId, recommendationStatus)
    ).pipe(
      tap(() => this.fetchRecommendations({ machine, faultCode })),
      map(() => undefined),
      catchError((error: ApiErrorResponse) => this.handleError(error))
    );
  }

  /**
   * Updates a recommendation in the local list after the API call succeeds.
   * The caller must subscribe to trigger the request.
   */
  updateRecommendation(recommendation: RecommendationDto): Observable<void> {
    return defer(() => this.troubleshootingApi.updateRecommendation(recommendation)).pipe(
      tap(() => this.replaceRecommendation(recommendation)),
      map(() => undefined),
      catchError((error: ApiErrorResponse) => this.handleError(error))
    );
  }

  /**
   * Validates a recommendation and merges the result into the local list after the API call succeeds.
   * The caller must subscribe to trigger the request.
   */
  validateRecommendation(recommendation: RecommendationDto): Observable<void> {
    return defer(() => this.troubleshootingApi.validateRecommendation(recommendation)).pipe(
      tap(() => this.replaceRecommendation(recommendation)),
      map(() => undefined),
      catchError((error: ApiErrorResponse) => this.handleError(error))
    );
  }

  /**
   * Deletes a recommendation and removes it from the local list after the API call succeeds.
   * The caller must subscribe to trigger the request.
   */
  deleteRecommendation(recommendationID: number): Observable<void> {
    return defer(() => this.troubleshootingApi.deleteRecommendation(recommendationID)).pipe(
      tap(() => {
        const recommendations = (this.state().recommendations ?? []).filter(({ id }) => id !== recommendationID);
        this.patch({ recommendations });
      }),
      map(() => undefined),
      catchError((error: ApiErrorResponse) => this.handleError(error))
    );
  }

  /**
   * Solves the problem with the selected recommendations and reloads the fault analysis.
   * The caller must subscribe to trigger the request.
   */
  solveProblem({
    machine,
    faultCode,
    selectedRecommendations,
    additionalNote,
    images = [],
  }: SolveProblemParams): Observable<void> {
    return defer(() =>
      this.problemSolvingApi.solveProblem({ machine, faultCode, selectedRecommendations, additionalNote, images })
    ).pipe(
      tap(() => {
        this.markProblemSolved();
        this.faultsStore.fetchFaultAnalysis({ machines: [] });
      }),
      map(() => undefined),
      catchError((error: ApiErrorResponse) => this.handleError(error))
    );
  }

  /**
   * Creates a recommendation and appends it to the local list after the API call succeeds.
   * The caller must subscribe to trigger the request.
   */
  createRecommendation(params: CreateRecommendationParams): Observable<RecommendationDto> {
    return defer(() => this.troubleshootingApi.createRecommendation(params)).pipe(
      tap((newRecommendation: RecommendationDto) => {
        if (newRecommendation) {
          this.patch({ recommendations: [...(this.state().recommendations ?? []), newRecommendation] });
        }
      }),
      catchError((error: ApiErrorResponse) => this.handleError(error))
    );
  }

  private replaceRecommendation(recommendation: RecommendationDto): void {
    const recommendations =
      this.state().recommendations?.map((current) =>
        current.id === recommendation.id ? { ...current, ...recommendation } : current
      ) ?? [];

    this.patch({ recommendations });
  }

  private markProblemSolved(): void {
    this.patch({ problemSolvedSuccess: true });
    setTimeout(() => this.patch({ problemSolvedSuccess: false }));
  }

  private reportFetchError(error: ApiErrorResponse): void {
    if (error?.headers) {
      this.errorHandler.handleError(error);
    }
  }

  private handleError(error: ApiErrorResponse): Observable<never> {
    if (error) {
      this.errorHandler.handleError(error);
    }
    return EMPTY;
  }

  private patch(partial: Partial<TroubleshootingStateModel>): void {
    this.state.update((current) => ({ ...current, ...partial }));
  }
}
